// © Microsoft Corporation. All rights reserved.

import { Direction, classNames } from "@microsoft/fast-web-utilities";
import { PublisherSubscribeFollowButtonTelemetryObjects, publisherSubscribeFollowButtonTelemetryContracts } from "./PublisherSubscribeFollowButton.telemetry";
import { addOrUpdateTelemetryObject, check_circled, enums, getExternalURL, getFlight, plus, publisherTabPremium, renderHybrid, shouldUseMoreFromPublisherFollowApi } from "@msnews/social-utils";

import React from "react";
import { SocialClassNameContract } from "../Social.classnames";
import { SocialProps } from "../Social.props";
import { hasPremiumSubscription } from "../utils/subscriptionHelpers";
import { initializeSubscriptionSdk } from "@msnews/subscription-sdk-wrapper";

export enum SubscribeButtonStateEnum {
    Following = "Following",
    Follow = "Follow",
    Subscribed = "Subscribed"
}

export interface PublisherSubscribeFollowButtonState {
    isSubscribed: Boolean,
    isFollowing: boolean  // used for "more-from publisher follow API"
}

const businessInsiderPublisherId = "AA28VO";

export default class PublisherSubscribeFollowButton extends React.Component<SocialProps, PublisherSubscribeFollowButtonState> {
    buttonConfig: {[key in SubscribeButtonStateEnum]: {text: string, icon: JSX.Element, managedClassName: keyof SocialClassNameContract}} = {
        [SubscribeButtonStateEnum.Follow]: {
            text: this.props.t.FollowButtonFollowText,
            icon: plus,
            managedClassName: "publisherSubscribeFollowButtonInactive"
        },
        [SubscribeButtonStateEnum.Following]: {
            text: this.props.t.FollowButtonFollowingText,
            icon: check_circled,
            managedClassName: "publisherSubscribeFollowButtonActive"
        },
        [SubscribeButtonStateEnum.Subscribed]: {
            text: this.props.t.PublisherProfilePageSubscribed,
            icon: publisherTabPremium,
            managedClassName: "publisherSubscribeFollowButtonActive"
        }
    };

    private subscriptionAndFollowChecked: boolean;
    private selfRef: React.RefObject<HTMLDivElement>;
    private shouldUseMoreFromPublisherFollowApi: boolean;
    private shouldRenderFollowButton: boolean;

    telemetryObjects: PublisherSubscribeFollowButtonTelemetryObjects;

    constructor(props) {
        super(props);
        this.selfRef = React.createRef();
        this.state = {
            isSubscribed: false,
            isFollowing: undefined
        };
        this.telemetryObjects = null;
        this.shouldRenderFollowButton = getFlight("prg-publv0") || getFlight("prg-publv1");
    }

    componentDidMount() {
        const { me, publisherSubscribeFollowButtonData } = this.props;
        if (!me) {
            this.props.usersConnector.fetchMe();
        } else if (publisherSubscribeFollowButtonData) {
            this.initializeComponent();
        }

        window.addEventListener("resize", this.alignSelf.bind(this));
    }

    componentDidUpdate(prevProps) {
        const { me, publisherSubscribeFollowButtonData } = this.props;
        if (!this.subscriptionAndFollowChecked && me && publisherSubscribeFollowButtonData) {
            this.initializeComponent();
        }
        this.alignSelf();
    }

    initializeComponent() {
        const { publisherSubscribeFollowButtonData } = this.props;
        if (this.shouldRenderFollowButton && publisherSubscribeFollowButtonData.publisherId === businessInsiderPublisherId) {
            initializeSubscriptionSdk();
        }
        this.checkSubscriptionAndFollowStatus();
    }

    async checkSubscriptionAndFollowStatus() {
        this.subscriptionAndFollowChecked = true;
        const { me, publisherSubscribeFollowButtonData } = this.props;
        const { fetchUser, fetchIsUserFollowed } = this.props.usersConnector;
        const { publisherId, publisherProfileId } = publisherSubscribeFollowButtonData;
        this.shouldUseMoreFromPublisherFollowApi = shouldUseMoreFromPublisherFollowApi(publisherId);

        if (this.shouldUseMoreFromPublisherFollowApi) {
            this.props.publisherSubscribeFollowButtonConnector.initializePublisherServiceClient();
            this.updateFollowStatusFromMoreFromApi();
        } else {
            fetchUser(publisherProfileId)
                .then(() => {
                    fetchIsUserFollowed(publisherProfileId);
                });
        }

        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (!isAnonymous) {
            hasPremiumSubscription(me, publisherProfileId).then(result => {
                this.setState(() => ({ isSubscribed: result }));
            });
        }

    }

    alignSelf() {
        // Aligns itself over web-component ViewsTitle in ViewsHeader (in views app-type, article-page experience)
        const container = document.querySelector("div[class*=viewsHeaderInfoLeft]");
        const titleWebComponent = document.getElementsByTagName("msnews-views-title")[0];

        if (!container || !titleWebComponent || !this.selfRef || !this.selfRef.current) { return; }

        const publisher = titleWebComponent.shadowRoot.querySelector(".providerInfoWrapper > a");

        const containerRect = container.getBoundingClientRect();
        const publisherRect = publisher.getBoundingClientRect();
        const titleWebComponentRect = titleWebComponent.getBoundingClientRect();

        if (document.dir === Direction.ltr) {
            const containerEnd = containerRect.x + containerRect.width;
            const publisherEnd = publisherRect.x + publisherRect.width;
            const margin = containerRect.width - (containerEnd - publisherEnd);
            this.selfRef.current.style.marginRight = "0px";
            this.selfRef.current.style.marginLeft = `${margin + 5}px`;
        } else {
            const containerEnd = containerRect.x + containerRect.width;
            const margin = (containerEnd - publisherRect.x);
            this.selfRef.current.style.marginLeft = "0px";
            this.selfRef.current.style.marginRight = `${margin + 5}px`;
        }
        this.selfRef.current.style.bottom = `${titleWebComponentRect.height - 12}px`;
    }

    render() {
        const { managedClasses, t, publisherSubscribeFollowButtonData, market } = this.props;
        const currentState = this.getCurrentState();

        if (!publisherSubscribeFollowButtonData || !currentState) { return null; }

        const config = this.getButtonConfig(currentState);
        const { publisherProfileId, publisherId } = publisherSubscribeFollowButtonData;
        const publisherPageUrl = getExternalURL(`${enums.externalUrls.channelPage}${publisherProfileId}`, market);
        const isBusinessInsider = publisherId === businessInsiderPublisherId;

        return (
            <div ref={this.selfRef} className={managedClasses.publisherSubscribeFollowButtonContainer}>
                { this.shouldRenderFollowButton &&
                    <button
                        aria-live="assertive"
                        type="button"
                        name={t.FollowButtonTitle}
                        className={classNames(
                            managedClasses.publisherSubscribeFollowButton,
                            managedClasses[config.managedClassName]
                        )}
                        onClick={(e) => this.onButtonClick(e)}
                        {...this.renderTelemetryTag(this.telemetryObjects)}
                    >
                        <i className="cwt-icon"> { config.icon } </i>
                        { config.text }
                    </button>
                }
                { isBusinessInsider &&
                    <a
                        className={managedClasses.publisherSubscribeFollowButtonViewProfileLink}
                        href={publisherPageUrl}
                        target="_blank"
                        {...renderHybrid(this.telemetryObjects && this.telemetryObjects.viewProfileLink, false)}
                    >
                        {t.PublisherSubscribeFollowButtonViewProfileText}
                    </a>
                }
            </div>
        );
    }

    onButtonClick(e) {
        const currentState = this.getCurrentState();
        if (currentState == SubscribeButtonStateEnum.Subscribed) {
            this.redirectToPublisherProfile();
        } else if (currentState == SubscribeButtonStateEnum.Following) {
            this.unfollowPublisher();
        } else {
            this.followPublisher();
        }
    }

    redirectToPublisherProfile() {
        const { publisherSubscribeFollowButtonData: { publisherProfileId }, market } = this.props;
        window.open(getExternalURL(`${enums.externalUrls.channelPage}${publisherProfileId}`, market));
    }

    async updateFollowStatusFromMoreFromApi() {
        const { publisherId } = this.props.publisherSubscribeFollowButtonData;
        const isFollowing = await this.props.publisherSubscribeFollowButtonConnector.getPublisherIsFollowing(publisherId);
        this.setState(() => ({ isFollowing }));
    }

    followPublisher() {
        const { me, publisherSubscribeFollowButtonData: { publisherProfileId, publisherId } } = this.props;

        if (this.shouldUseMoreFromPublisherFollowApi) {
            this.props.publisherSubscribeFollowButtonConnector.followPublisher(publisherId, true).then(
                () => this.updateFollowStatusFromMoreFromApi()
            );
            return;
        }

        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) {
            this.signIn();
        } else {
            this.props.usersConnector.followUser(publisherProfileId, true);
        }
    }

    unfollowPublisher() {
        const { publisherSubscribeFollowButtonData: { publisherProfileId, publisherId } } = this.props;

        if (this.shouldUseMoreFromPublisherFollowApi) {
            this.props.publisherSubscribeFollowButtonConnector.followPublisher(publisherId, false).then(
                () => this.updateFollowStatusFromMoreFromApi()
            );
        } else {
            this.props.usersConnector.followUser(publisherProfileId, false);
        }
    }

    getCurrentState() {
        const { me, publisherSubscribeFollowButtonData, users } = this.props;
        const publisherProfileId = publisherSubscribeFollowButtonData?.publisherProfileId;

        let isFollowing: boolean;
        if (this.shouldUseMoreFromPublisherFollowApi) {
            if (this.state.isFollowing == undefined) {
                return null;
            }
            isFollowing = this.state.isFollowing;
        } else {
            if (!publisherSubscribeFollowButtonData || !publisherProfileId || !users || !users[publisherProfileId] || !users[publisherProfileId].isFollowingFetched) {
                return null;
            }

            const isAnonymous = me && me.state == enums.userStateType.anonymous;
            if (isAnonymous) {
                return SubscribeButtonStateEnum.Follow;
            }

            isFollowing = users[publisherProfileId].data?.isFollowing;
        }

        if (this.state.isSubscribed) {
            return SubscribeButtonStateEnum.Subscribed;
        } else {
            return isFollowing ? SubscribeButtonStateEnum.Following : SubscribeButtonStateEnum.Follow;
        }
    }

    getButtonConfig(state: SubscribeButtonStateEnum) {
        return this.buttonConfig[state];
    }

    signIn() {
        this.props.popupConnector.openPopup("multiLoginPage");
    }

    renderTelemetryTag(telemetryObject) {

        const { publisherSubscribeFollowButtonData, mainTelemetryObject } = this.props;
        const { isSubscribed } = this.state;
        const isFollowing = this.getCurrentState() == SubscribeButtonStateEnum.Following;
        const id = publisherSubscribeFollowButtonData.publisherId;

        if (mainTelemetryObject) {
            this.telemetryObjects = {
                publisherSubscribeFollowButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...publisherSubscribeFollowButtonTelemetryContracts.publisherSubscribeFollowButton}, { id }, this.telemetryObjects && this.telemetryObjects.publisherSubscribeFollowButton),
                publisherSubscribeFollowingButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...publisherSubscribeFollowButtonTelemetryContracts.publisherSubscribeFollowingButton}, { id }, this.telemetryObjects && this.telemetryObjects.publisherSubscribeFollowingButton),
                publisherSubscribeSubscribedButton: addOrUpdateTelemetryObject(mainTelemetryObject, {...publisherSubscribeFollowButtonTelemetryContracts.publisherSubscribeSubscribedButton}, { id }, this.telemetryObjects && this.telemetryObjects.publisherSubscribeSubscribedButton),
                viewProfileLink: addOrUpdateTelemetryObject(mainTelemetryObject, {...publisherSubscribeFollowButtonTelemetryContracts.viewProfileLink}, { id }, this.telemetryObjects && this.telemetryObjects.viewProfileLink)
            };
        }

        if (isSubscribed) {
            telemetryObject= this.telemetryObjects.publisherSubscribeSubscribedButton;
            return {...renderHybrid(this.telemetryObjects && telemetryObject, false)};
        } else {
            telemetryObject = isFollowing ? this.telemetryObjects.publisherSubscribeFollowingButton : this.telemetryObjects.publisherSubscribeFollowButton;
            return {...renderHybrid(this.telemetryObjects && telemetryObject, false)};
        }

    }
}
